import React, { type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { LoadingButton } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import Icon from '@atlaskit/icon';
import AssetsGraph from '@atlaskit/icon-lab/core/assets-graph';
import ShortcutIcon from '@atlaskit/icon/core/link-external';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { getServiceDetailsUrl } from '@atlassian/jira-servicedesk-navigation/src/utils/service-registry/index.tsx';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import { isPremiumInstance } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/services/resources/get-data/index.tsx';
import { graphModalExperience } from '@atlassian/jira-servicedesk-insight-experiences/src/index.tsx';
import type { CmdbObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import { ServicesGlyph } from '@atlassian/jira-servicedesk-work-category/src/common/assets/glyph.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { BasicServiceDetails } from '../../common/types/index.tsx';
import { ActiveIncidentsIndicator } from '../../common/ui/active-incidents-indicator/index.tsx';
import { useFetchObjectDetails } from '../../services/fetch-object-details/index.tsx';
import messages from './messages.tsx';

interface ValueType<T> {
	id: string;
	name: string;
	data: T;
}

export type DetailButtonProps = {
	value: ValueType<BasicServiceDetails>;
	onViewObjectDetails: (arg1: CmdbObject, arg2: UIAnalyticsEvent) => void;
};

const DetailsButton = (props: DetailButtonProps) => {
	const {
		value: { id },
		onViewObjectDetails,
	} = props;

	const { formatMessage } = useIntl();

	const { workspaceId } = useWorkspaceContext();

	const buttonLabel = formatMessage(messages.viewFullServiceDetails);

	const { loading: isLoading, fetch } = useFetchObjectDetails(workspaceId, id);

	const handleViewObjectDetails = (
		event: SyntheticEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		graphModalExperience.start();

		fetch().then((cmdbObject?: CmdbObject) => {
			if (!cmdbObject) {
				return;
			}
			onViewObjectDetails(cmdbObject, analyticsEvent);
		});
	};

	return (
		<Tooltip
			content={buttonLabel}
			testId="issue-field-service-entity.ui.header-content.details-button"
		>
			<LoadingButton
				onClick={handleViewObjectDetails}
				appearance="subtle"
				iconBefore={<AssetsGraph spacing="spacious" label={buttonLabel} />}
				isLoading={isLoading}
				testId="issue-field-service-entity.ui.header-content.graph-button"
				interactionName="auto-generated.issue-field-service-entity.ui.header-content.graph-button.button"
				aria-label={buttonLabel}
			/>
		</Tooltip>
	);
};

export type Props = {
	isEditing: boolean;
	isExpanded: boolean;
	projectKey: ProjectKey;
	value: ValueType<BasicServiceDetails>;
	onViewObjectDetails: (arg1: CmdbObject, arg2: UIAnalyticsEvent) => void;
};

// HeaderContent uses only id and name of the service already.
export const HeaderContent = (props: Props): React.JSX.Element | null => {
	const { value, isEditing, isExpanded, projectKey, onViewObjectDetails } = props;
	const { formatMessage } = useIntl();

	const tenantContext = useTenantContext();
	const isPremium = isPremiumInstance(tenantContext);

	if (value == null) {
		return null;
	}

	const buttonLabel = formatMessage(messages.viewFullServiceDetails);
	const ariaLabel = formatMessage(messages.ariaLabel);

	const buttons = [];
	if (!isEditing) {
		if (!isExpanded) {
			buttons.push(<ActiveIncidentsIndicator services={[value.data]} />);
		}
		if (isPremium) {
			buttons.push(<DetailsButton value={value} onViewObjectDetails={onViewObjectDetails} />);
		}
	}

	return (
		<Header>
			<Label>
				{/* eslint-disable-next-line @atlaskit/design-system/no-custom-icons */}
				<Icon
					/* @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'. */
					glyph={ServicesGlyph}
					label={value.name}
					testId="issue-field-service-entity.ui.header-content.icon"
				/>
				<Tooltip content={buttonLabel}>
					<DetailLink
						href={getServiceDetailsUrl(projectKey, value.id)}
						target="_blank"
						aria-label={ariaLabel}
					>
						<Heading size="xsmall" as="div">
							<NameComponent>{value.name}</NameComponent>
						</Heading>
						<ShortcutIcon
							label=""
							testId="issue-field-service-entity.ui.header-content.shortcut-icon"
						/>
					</DetailLink>
				</Tooltip>
			</Label>
			{buttons.map((button, index) => (
				<ButtonWrapper key={index}>{button}</ButtonWrapper>
			))}
		</Header>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginTop: token('space.negative.050'),
	marginBottom: token('space.negative.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DetailLink = styled.a({
	color: token('color.link'),
	display: 'flex',
	alignItems: 'center',
	paddingRight: token('space.050'),
	paddingLeft: token('space.050'),
	marginRight: token('space.050'),
	marginLeft: token('space.050'),
	'&:focus': {
		borderRadius: token('radius.small'),
	},
	'&:focus, &:hover, &:active': {
		color: token('color.link.pressed'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:focus > span, &:hover > span': {
		visibility: 'visible',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > span': {
		visibility: 'hidden',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NameComponent = styled.div({
	color: token('color.text.subtle'),
	margin: 0,
	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Label = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	flexGrow: 1,

	width: 'calc(100% - 80px)',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:not(:last-child)': {
		marginRight: token('space.100'),
	},
});
